export type ObjectCategory =
  | 'furniture'
  | 'industrial'
  | 'fixture'
  | 'effects'
  | 'gameplay'
  | 'structural';

export type ParamSchema =
  | {
      type: 'number';
      min?: number;
      max?: number;
      step?: number;
      integer?: boolean;
    }
  | { type: 'string'; maxLength?: number }
  | { type: 'enum'; options: readonly string[] }
  | { type: 'color' }
  | { type: 'boolean' };

export interface ObjectDefinition {
  id: string;
  name: string;
  category: ObjectCategory;
  description: string;
  bounds: {
    size: [number, number, number];
    blockerFootprint?: [number, number];
  };
  defaultParams?: Record<string, unknown>;
  editor: {
    pivot: 'base-center' | 'center';
    snap?: number;
    tags?: string[];
    /**
     * Who made this and what pack it ships in - drives the Library tab's
     * author/bundle grouping (engine/tools/object-browser/ObjectBrowser.tsx)
     * and its hover-card attribution line. Both unset for a definition with
     * no known provenance (falls back to parsing `description` for legacy
     * "Author · Bundle (licence)" strings, e.g. the shared library pack).
     */
    author?: string;
    bundle?: string;
    /**
     * Optional hand-authored thumbnail URL or data-URI. When set, the editor
     * library uses this instead of the auto-generated 3D render.
     */
    thumbnailOverride?: string;
    /**
     * Optional offset applied to the camera target when generating a thumbnail.
     * Useful for tall or asymmetric objects where the default bounding-box
     * center crops the silhouette awkwardly.
     */
    thumbnailTargetOffset?: [number, number, number];
    /**
     * Optional per-parameter validation schema. Drives the inspector UI,
     * clamps nudged values, and powers pre-save validation.
     */
    paramSchema?: Record<string, ParamSchema>;
  };
}

/** The one shared source of truth for the platform's own first-party
 * attribution - every built-in/Core pack definition's `editor.author`/
 * `editor.bundle` should use these, not a re-typed literal, so the Library
 * tab's pinned author entry (ObjectBrowser.tsx's `pinOriginals`, which
 * filters on this exact author string) can't drift out of sync with what
 * actually gets registered. */
export const SOULSTACK_ORIGINALS_AUTHOR = 'Soulstack Originals';
export const SOULSTACK_ORIGINALS_BUNDLE = 'Core Pack';

/**
 * The engine's built-in, game-agnostic object definitions: lights, gameplay
 * triggers, markers, and surface decals. Games register their own packs (props,
 * furniture, etc.) via `registerObjects`. Rendering is resolved separately
 * through the renderer registry (`objectRenderers`).
 */
export const BUILTIN_OBJECT_DEFINITIONS: Record<string, ObjectDefinition> = {
  // ── Lights ──────────────────────────────────────────────────────────────────
  light_point_warm: {
    id: 'light_point_warm',
    name: 'Warm Point Light',
    category: 'fixture',
    description: 'Warm ambient point light for atmospheric fill.',
    bounds: { size: [0.2, 0.2, 0.2] },
    defaultParams: { intensity: 1.2, range: 8, color: '#ffd9a8' },
    editor: { pivot: 'center', snap: 0.1, tags: ['lighting', 'warm'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  light_point_cool: {
    id: 'light_point_cool',
    name: 'Cool Point Light',
    category: 'fixture',
    description: 'Cool blue-white point light.',
    bounds: { size: [0.2, 0.2, 0.2] },
    defaultParams: { intensity: 1.0, range: 6, color: '#c8e0ff' },
    editor: { pivot: 'center', snap: 0.1, tags: ['lighting', 'cool'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  light_spot: {
    id: 'light_spot',
    name: 'Spot Light',
    category: 'fixture',
    description: 'Directional spot light with cone angle.',
    bounds: { size: [0.2, 0.2, 0.2] },
    defaultParams: { intensity: 2.0, range: 10, angle: 0.4, color: '#fff8e8' },
    editor: { pivot: 'center', snap: 0.1, tags: ['lighting', 'directional'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },

  // ── Gameplay triggers ────────────────────────────────────────────────────────
  trigger_teleport: {
    id: 'trigger_teleport',
    name: 'Teleport Trigger',
    category: 'gameplay',
    description: 'Teleports the player to a target floor and position.',
    bounds: { size: [2, 2.4, 2] },
    defaultParams: { targetFloor: 0, targetX: 0, targetZ: 0 },
    editor: { pivot: 'center', snap: 0.1, tags: ['trigger', 'navigation'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  trigger_event: {
    id: 'trigger_event',
    name: 'Event Trigger',
    category: 'gameplay',
    description: 'Fires a named game event when the player enters.',
    bounds: { size: [2, 2.4, 2] },
    defaultParams: { eventName: 'custom-event', oneShot: true },
    editor: { pivot: 'center', snap: 0.1, tags: ['trigger', 'event'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  trigger_jumpscare: {
    id: 'trigger_jumpscare',
    name: 'Jumpscare Trigger',
    category: 'gameplay',
    description: 'Triggers a jumpscare sequence when entered.',
    bounds: { size: [1.6, 2.4, 1.6] },
    defaultParams: { jumpscareId: 'default', oneShot: true },
    editor: { pivot: 'center', snap: 0.1, tags: ['trigger', 'horror', 'jumpscare'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },

  // ── Markers ──────────────────────────────────────────────────────────────────
  marker_spawn: {
    id: 'marker_spawn',
    name: 'Spawn Point',
    category: 'gameplay',
    description: 'Player spawn location marker.',
    bounds: { size: [0.5, 1.8, 0.5] },
    editor: { pivot: 'base-center', snap: 0.1, tags: ['marker', 'spawn'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  marker_checkpoint: {
    id: 'marker_checkpoint',
    name: 'Checkpoint',
    category: 'gameplay',
    description: 'Save/checkpoint marker.',
    bounds: { size: [0.5, 1.8, 0.5] },
    defaultParams: { checkpointId: 'cp-1' },
    editor: { pivot: 'base-center', snap: 0.1, tags: ['marker', 'checkpoint'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  marker_event: {
    id: 'marker_event',
    name: 'Event Marker',
    category: 'gameplay',
    description: 'Named event location for scripted sequences.',
    bounds: { size: [0.5, 1.8, 0.5] },
    defaultParams: { eventName: 'scene-event' },
    editor: { pivot: 'base-center', snap: 0.1, tags: ['marker', 'event'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },

  // ── Decals ───────────────────────────────────────────────────────────────────
  decal_wet_floor: {
    id: 'decal_wet_floor',
    name: 'Wet Floor',
    category: 'effects',
    description: 'Wet floor decal for atmospheric detail.',
    bounds: { size: [2, 0.02, 1.4] },
    defaultParams: { opacity: 0.6 },
    editor: { pivot: 'center', snap: 0.05, tags: ['decal', 'floor', 'wet'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  decal_stain: {
    id: 'decal_stain',
    name: 'Floor Stain',
    category: 'effects',
    description: 'Dark stain or spill decal.',
    bounds: { size: [1.2, 0.02, 0.9] },
    defaultParams: { opacity: 0.7 },
    editor: { pivot: 'center', snap: 0.05, tags: ['decal', 'floor', 'horror'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
  decal_crack: {
    id: 'decal_crack',
    name: 'Wall Crack',
    category: 'effects',
    description: 'Crack or damage decal for walls.',
    bounds: { size: [0.8, 1.2, 0.02] },
    defaultParams: { opacity: 0.8 },
    editor: { pivot: 'center', snap: 0.05, tags: ['decal', 'wall', 'damage'], author: SOULSTACK_ORIGINALS_AUTHOR, bundle: SOULSTACK_ORIGINALS_BUNDLE },
  },
};

// ── Mutable registry ──────────────────────────────────────────────────────────
// Seeded with the engine built-ins; games add their packs via registerObjects.
const REGISTRY = new Map<string, ObjectDefinition>(
  Object.entries(BUILTIN_OBJECT_DEFINITIONS),
);

// Reactive surface so editor UIs (palette, thumbnails) refresh when a project
// registers new definitions at runtime (e.g. an uploaded GLB).
const listeners = new Set<() => void>();
let registryVersion = 0;

/** Subscribe to registry mutations (useSyncExternalStore-compatible). */
export function subscribeObjectRegistry(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Monotonic version bumped on every registration. */
export function getObjectRegistryVersion(): number {
  return registryVersion;
}

/** Register (or override) object definitions. Idempotent per id. */
export function registerObjects(defs: Record<string, ObjectDefinition> | ObjectDefinition[]): void {
  const list = Array.isArray(defs) ? defs : Object.values(defs);
  for (const def of list) {
    REGISTRY.set(def.id, def);
  }
  registryVersion += 1;
  for (const listener of listeners) listener();
}

export function getObjectDefinition(assetId: string): ObjectDefinition | null {
  return REGISTRY.get(assetId) ?? null;
}

export function listObjectDefinitions(): ObjectDefinition[] {
  return [...REGISTRY.values()];
}
